import React from 'react';
import {
  Box,
  Chip,
  List,
  ListItem,
  Typography,
  Alert,
} from '@mui/material';
import {
  OpenInBrowser as NavIcon,
  TouchApp as InteractIcon,
  FactCheck as AssertIcon,
  HourglassEmpty as WaitIcon,
  Input as DataIcon,
  CameraAlt as ScreenshotIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
} from '@mui/icons-material';
import { NLTestPlan, NLStepResult } from '../types';

interface NLTestStepsViewerProps {
  plan: NLTestPlan;
  stepResults?: NLStepResult[];
  compact?: boolean;
}

const ACTION_CATEGORIES: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  navigate: { icon: <NavIcon fontSize="small" />, color: '#1976d2', label: 'Navigation' },
  goto: { icon: <NavIcon fontSize="small" />, color: '#1976d2', label: 'Navigation' },
  click: { icon: <InteractIcon fontSize="small" />, color: '#2e7d32', label: 'Interaction' },
  type: { icon: <DataIcon fontSize="small" />, color: '#7b1fa2', label: 'Data' },
  fill: { icon: <DataIcon fontSize="small" />, color: '#7b1fa2', label: 'Data' },
  select: { icon: <InteractIcon fontSize="small" />, color: '#2e7d32', label: 'Interaction' },
  check: { icon: <InteractIcon fontSize="small" />, color: '#2e7d32', label: 'Interaction' },
  assert: { icon: <AssertIcon fontSize="small" />, color: '#ed6c02', label: 'Assertion' },
  verify: { icon: <AssertIcon fontSize="small" />, color: '#ed6c02', label: 'Assertion' },
  expect: { icon: <AssertIcon fontSize="small" />, color: '#ed6c02', label: 'Assertion' },
  wait: { icon: <WaitIcon fontSize="small" />, color: '#757575', label: 'Wait' },
  screenshot: { icon: <ScreenshotIcon fontSize="small" />, color: '#00897b', label: 'Screenshot' },
  hover: { icon: <InteractIcon fontSize="small" />, color: '#2e7d32', label: 'Interaction' },
  scroll: { icon: <InteractIcon fontSize="small" />, color: '#2e7d32', label: 'Interaction' },
  press: { icon: <InteractIcon fontSize="small" />, color: '#2e7d32', label: 'Interaction' },
};

const getActionCategory = (actionId: string) => {
  const lower = actionId.toLowerCase();
  for (const [key, value] of Object.entries(ACTION_CATEGORIES)) {
    if (lower.includes(key)) return value;
  }
  return { icon: <InteractIcon fontSize="small" />, color: '#757575', label: actionId };
};

const NLTestStepsViewer: React.FC<NLTestStepsViewerProps> = ({ plan, stepResults, compact }) => {
  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Header */}
      {!compact && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {plan.description}
          </Typography>
          {plan.base_url && (
            <Chip
              size="small"
              label={plan.base_url}
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 1 }}
            />
          )}
        </Box>
      )}

      {/* Validation warnings */}
      {plan.validation_warnings && plan.validation_warnings.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {plan.validation_warnings.map((warning, idx) => (
            <Alert key={idx} severity="warning" sx={{ py: 0, mb: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              {warning}
            </Alert>
          ))}
        </Box>
      )}

      {/* Steps list */}
      <List dense disablePadding>
        {plan.steps.map((step, idx) => {
          const category = getActionCategory(step.action_id);
          const result = stepResults?.find((r) => r.step_index === idx);
          const selectorParam = step.parameters?.selector || step.parameters?.url;

          return (
            <ListItem
              key={idx}
              disablePadding
              sx={{
                py: compact ? 0.25 : 0.5,
                px: 1,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: result
                  ? result.status === 'passed'
                    ? 'success.light'
                    : result.status === 'failed'
                    ? 'error.light'
                    : 'transparent'
                  : 'transparent',
                border: '1px solid',
                borderColor: result
                  ? result.status === 'passed'
                    ? 'success.main'
                    : result.status === 'failed'
                    ? 'error.main'
                    : 'divider'
                  : 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                {/* Step number */}
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 20, textAlign: 'right' }}
                >
                  {idx + 1}.
                </Typography>

                {/* Result icon */}
                {result && (
                  result.status === 'passed' ? (
                    <PassIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
                  ) : result.status === 'failed' ? (
                    <FailIcon fontSize="small" color="error" sx={{ flexShrink: 0 }} />
                  ) : null
                )}

                {/* Action icon + chip */}
                <Chip
                  icon={category.icon as React.ReactElement}
                  label={step.action_id}
                  size="small"
                  sx={{
                    bgcolor: category.color + '18',
                    color: category.color,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                    flexShrink: 0,
                    '& .MuiChip-icon': { color: category.color },
                  }}
                />

                {/* Description */}
                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap={compact}>
                  {step.description}
                </Typography>

                {/* Selector/URL in monospace */}
                {selectorParam && !compact && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.secondary',
                      bgcolor: 'grey.100',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.7rem',
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {String(selectorParam).length > 40
                      ? String(selectorParam).slice(0, 40) + '...'
                      : String(selectorParam)}
                  </Typography>
                )}

                {/* Duration */}
                {result?.duration_ms != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {result.duration_ms}ms
                  </Typography>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>

      {/* Summary */}
      {!compact && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {plan.step_count || plan.steps.length} steps
        </Typography>
      )}
    </Box>
  );
};

export default NLTestStepsViewer;
